import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useHotelAdminStore } from '../store'
import { apiClient } from '../services/apiClient'
import { ROUTES } from '../constants/routes'

interface SubscriptionStatus {
  admin_id: number
  has_active_subscription: boolean
  subscription_status: 'active' | 'pending' | 'expired'
  subscription_expires_at: string | null
  last_payment_date: string | null
  tariff_id: string | null
  days_remaining: number
}

export default function AdminPaymentSuccessPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { admin, setAdmin, getAccessToken } = useHotelAdminStore()

  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionStatus | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        setIsLoading(true)

        // Get YooKassa payment ID from URL if available
        const invoiceId = searchParams.get('invoice_id')
        console.log('Invoice ID from URL:', invoiceId)

        // 🔑 Получаем токен из localStorage, это безопаснее чем хранить в state
        const accessToken = getAccessToken()
        
        if (!accessToken) {
          setStatus('error')
          setError('Ошибка авторизации. Пожалуйста, перезагрузитесь.')
          setIsLoading(false)
          return
        }

        // Check subscription status via API
        const response = await apiClient.get(
          '/payments/subscription'
        )

if (response.status === 'ok' && response.data) {
          const subData = response.data as SubscriptionStatus
          setSubscriptionInfo(subData)

          if (
            subData.subscription_status === 'active'
          ) {
            setStatus('success')

            // Update admin store with latest subscription info
            if (admin) {
              setAdmin({
                ...admin,
                subscription_status: subData.subscription_status,
                subscription_expires_at: subData.subscription_expires_at,
              })
            }
          } else if (subData.subscription_status === 'pending') {
            setStatus('loading')
            setError(
              'Платеж обрабатывается. Пожалуйста, подождите несколько минут и обновите страницу.'
            )
          } else {
            setStatus('error')
            setError('Ошибка при активации подписки. Пожалуйста, обратитесь в поддержку.')
          }
        } else if (response.status === 'ok' && !response.data) {
          console.warn('🔵 Response has ok status but no data field')
          setStatus('error')
          setError('Ошибка: сервер не вернул данные подписки. Пожалуйста, обновите страницу.')
        } else {
          console.warn('🔴 Response status is not ok:', response.status)
          setStatus('error')
          setError('Ошибка при проверке статуса подписки')
        }
      } catch (err: any) {
        console.error('🔴 Payment status check error:', err)
        console.error('🔴 Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
        })
        setStatus('error')
        setError(
          err.response?.data?.message ||
          err.message ||
          'Ошибка при проверке статуса платежа. Пожалуйста, обратитесь в поддержку.'
        )
      } finally {
        setIsLoading(false)
      }
    }

    // Check payment status after a short delay to allow webhook processing
    const timeout = setTimeout(checkPaymentStatus, 2000)
    return () => clearTimeout(timeout)
  }, [getAccessToken, setAdmin])

  const handleRedirectToDashboard = () => {
    navigate(ROUTES.ADMIN_DASHBOARD)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-600 to-blue-600 p-4">
        <div className="bg-white rounded-lg shadow-lg p-12 max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-indigo-600 animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Обработка платежа</h2>
          <p className="text-gray-600">Пожалуйста, подождите...</p>
        </div>
      </div>
    )
  }

  if (status === 'success' && subscriptionInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-600 to-emerald-600 p-4">
        <div className="bg-white rounded-lg shadow-2xl p-12 max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-green-600 mb-3">✅ Успешно!</h2>
          <p className="text-gray-600 mb-2">Ваша подписка активирована</p>
          <p className="text-sm text-gray-500 mb-6">
            Спасибо за оплату! Подписка действительна до{' '}
            <span className="font-semibold">
              {new Date(subscriptionInfo.subscription_expires_at).toLocaleDateString('ru-RU')}
            </span>
          </p>

          <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Тариф:</span> {subscriptionInfo.tariff_id}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Статус:</span> Активна
            </p>
          </div>

          <button
            onClick={handleRedirectToDashboard}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
          >
            💳 Перейти в панель администратора
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-600 to-pink-600 p-4">
      <div className="bg-white rounded-lg shadow-2xl p-12 max-w-md text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-red-600 mb-3">Ошибка</h2>
        <p className="text-gray-600 mb-6">{error}</p>

        <div className="space-y-3">
          <button
            onClick={handleRedirectToDashboard}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
          >
            ← Вернуться в панель
          </button>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
          >
            🔄 Обновить страницу
          </button>
        </div>
      </div>
    </div>
  )
}
